import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { AlertTriangle, X, ChevronRight } from "lucide-react";

const POLL_MS = 60_000;

function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
}

/**
 * Job Health banner. Sits below the top bar. Renders nothing when there are no alerts.
 * Polls /api/alerts/jobs on route change and every 60s. Alerts can be dismissed;
 * dismiss snapshots the current stage so the alert only re-fires if it escalates
 * (e.g. warning → over).
 */
export default function AlertsBanner() {
  const nav = useNavigate();
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [dismissBusy, setDismissBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts/jobs");
      setAlerts(data.alerts || []);
    } catch (err) {
      devLog.warn("alerts load failed", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, location.pathname]);

  useEffect(() => {
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const current = alerts[0];
  if (!current) return null;

  const over = current.stage === "over";
  const color = over ? "#F87171" : "#FBBF24";
  const label = over
    ? `${current.name} is over budget`
    : `${current.name} is at ${current.pct.toFixed(0)}% of budget`;
  const sub = over
    ? `${money(current.spent - current.budget)} over — ${money(current.budget)} budget`
    : `${money(current.remaining)} left of ${money(current.budget)}`;

  const dismiss = async () => {
    setDismissBusy(true);
    try {
      await api.post(`/alerts/jobs/${current.job_id}/ack`);
      setAlerts((prev) => prev.filter((a) => a.job_id !== current.job_id));
    } catch (err) {
      devLog.warn("ack failed", err);
    } finally {
      setDismissBusy(false);
    }
  };

  return (
    <div
      data-testid="alerts-banner"
      className="rounded-xl p-3 mb-3 border relative overflow-hidden"
      style={{
        borderColor: `${color}55`,
        background: over ? "rgba(248,113,113,0.09)" : "rgba(251,191,36,0.08)",
      }}
    >
      <div
        className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: color }}
      />
      <div className="relative flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${color}22`, color }}
        >
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-heading" style={{ color }}>
            {over ? "Over budget" : "Job health · warning"}
            {alerts.length > 1 && (
              <span className="ml-2 text-white/50">+{alerts.length - 1} more</span>
            )}
          </div>
          <div className="font-heading font-extrabold text-base leading-tight truncate">
            {label}
          </div>
          <div className="text-xs text-white/60 truncate">{sub}</div>
        </div>
        <button
          type="button"
          onClick={() => nav("/jobs")}
          data-testid="alerts-banner-view"
          className="h-9 px-3 rounded-lg text-xs font-semibold shrink-0 inline-flex items-center gap-1"
          style={{ background: color, color: "#111827" }}
        >
          View <ChevronRight className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          disabled={dismissBusy}
          data-testid="alerts-banner-dismiss"
          className="w-9 h-9 rounded-lg text-white/40 hover:text-white/80 flex items-center justify-center shrink-0 disabled:opacity-50"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
